import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadCsv, type CsvColumn } from "@/lib/csv";
import { formatDateTime, formatRelativeTime } from "@/lib/format";
import type { FaqContentField, FaqVersionContent, FaqVersionRow } from "@/pages/help/faqContent";

interface FaqVersionHistoryProps {
  versions: FaqVersionRow[];
  loading: boolean;
  saving: boolean;
  questionLabel: string;
  onRollback: (version: FaqVersionRow) => void;
}

const FIELD_KEYS: Record<FaqContentField, { langKey: string; labelKey: string }> = {
  question_zh: { langKey: "helpFeedback.contentLangZh", labelKey: "helpFeedback.contentQuestion" },
  answer_zh: { langKey: "helpFeedback.contentLangZh", labelKey: "helpFeedback.contentAnswer" },
  question_en: { langKey: "helpFeedback.contentLangEn", labelKey: "helpFeedback.contentQuestion" },
  answer_en: { langKey: "helpFeedback.contentLangEn", labelKey: "helpFeedback.contentAnswer" },
};

const ACTION_LABELS: Record<string, string> = {
  publish: "helpFeedback.actionPublish",
  draft: "helpFeedback.actionDraft",
  reset: "helpFeedback.actionReset",
  rollback: "helpFeedback.actionRollback",
};

const ACTION_TONES: Record<string, string> = {
  publish: "bg-primary-100 text-primary-700",
  draft: "bg-secondary-100 text-secondary-900",
  reset: "bg-background-200 text-foreground-700",
  rollback: "bg-accent-100 text-accent-700",
};

const ALL = "__all__";

const selectClass =
  "cursor-pointer rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-700 outline-none transition-colors focus:border-primary-400";

export default function FaqVersionHistory({
  versions,
  loading,
  saving,
  questionLabel,
  onRollback,
}: FaqVersionHistoryProps) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editorFilter, setEditorFilter] = useState(ALL);
  const [actionFilter, setActionFilter] = useState(ALL);

  const fieldLabel = (field: FaqContentField) =>
    `${t(FIELD_KEYS[field].langKey)} · ${t(FIELD_KEYS[field].labelKey)}`;

  const actionLabel = (action: string) => t(ACTION_LABELS[action] ?? "helpFeedback.actionPublish");

  const changedLabel = (fields: FaqContentField[]) =>
    fields.length > 0 ? fields.map((field) => fieldLabel(field)).join(" / ") : t("helpFeedback.diffNoChange");

  const editorOptions = useMemo(() => {
    const set = new Set<string>();
    versions.forEach((version) => {
      if (version.editor) set.add(version.editor);
    });
    return Array.from(set);
  }, [versions]);

  const actionOptions = useMemo(() => {
    const set = new Set<string>();
    versions.forEach((version) => {
      if (version.action) set.add(version.action);
    });
    return Array.from(set);
  }, [versions]);

  const filtered = useMemo(() => {
    return versions.filter((version) => {
      if (editorFilter !== ALL && (version.editor ?? "") !== editorFilter) return false;
      if (actionFilter !== ALL && version.action !== actionFilter) return false;
      return true;
    });
  }, [versions, editorFilter, actionFilter]);

  const exportLedger = () => {
    const columns: CsvColumn<FaqVersionRow>[] = [
      { key: "time", label: t("helpFeedback.ledgerColTime"), value: (row) => formatDateTime(row.created_at) },
      { key: "question", label: t("helpFeedback.ledgerColQuestion"), value: () => questionLabel },
      { key: "editor", label: t("helpFeedback.ledgerColEditor"), value: (row) => row.editor ?? t("helpFeedback.unknownEditor") },
      { key: "action", label: t("helpFeedback.ledgerColAction"), value: (row) => actionLabel(row.action) },
      {
        key: "fields",
        label: t("helpFeedback.ledgerColFields"),
        value: (row) => changedLabel((row.changed_fields ?? []) as FaqContentField[]),
      },
    ];
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    downloadCsv(`faq-change-ledger-${stamp}.csv`, columns, filtered);
  };

  const contentValue = (field: FaqContentField, content: Partial<FaqVersionContent> | undefined) => {
    const value = content?.[field];
    if (value && value.trim()) return value;
    return t("helpFeedback.diffDefault");
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((item) => (
          <div key={item} className="h-16 w-full animate-pulse rounded-lg bg-background-100" />
        ))}
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-400">
          <i className="ri-history-line text-xl" />
        </span>
        <p className="mt-3 text-sm font-medium text-foreground-950">{t("helpFeedback.versionsEmpty")}</p>
        <p className="mt-1 text-xs text-foreground-600">{t("helpFeedback.versionsEmptyDesc")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border border-background-200 bg-background-100/60 px-3 py-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-foreground-600">{t("helpFeedback.filterEditor")}</span>
            <select value={editorFilter} onChange={(event) => setEditorFilter(event.target.value)} className={selectClass}>
              <option value={ALL}>{t("helpFeedback.filterAllEditors")}</option>
              {editorOptions.map((editor) => (
                <option key={editor} value={editor}>
                  {editor}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-foreground-600">{t("helpFeedback.filterAction")}</span>
            <select value={actionFilter} onChange={(event) => setActionFilter(event.target.value)} className={selectClass}>
              <option value={ALL}>{t("helpFeedback.filterAllActions")}</option>
              {actionOptions.map((action) => (
                <option key={action} value={action}>
                  {actionLabel(action)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button
          type="button"
          onClick={exportLedger}
          disabled={filtered.length === 0}
          title={t("helpFeedback.exportLedgerTitle")}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-download-2-line" />
          </span>
          {t("helpFeedback.exportLedger")}
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-filter-3-line text-lg" />
          </span>
          <p className="mt-3 text-sm font-medium text-foreground-950">{t("helpFeedback.versionFilterEmpty")}</p>
        </div>
      ) : (
        filtered.map((version, index) => {
          const older = filtered[index + 1];
          const changed = (version.changed_fields ?? []) as FaqContentField[];
          const isOpen = expandedId === version.id;
          return (
            <div key={version.id} className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 items-start gap-2.5">
                  <span
                    className={`mt-0.5 inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                      ACTION_TONES[version.action] ?? "bg-background-200 text-foreground-700"
                    }`}
                  >
                    {actionLabel(version.action)}
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-600">
                      <span className="text-foreground-800">{version.editor || t("helpFeedback.unknownEditor")}</span>
                      <span className="mx-1.5 text-foreground-400">·</span>
                      <span title={formatDateTime(version.created_at)}>{formatRelativeTime(version.created_at)}</span>
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {changed.length > 0 ? (
                        changed.map((field) => (
                          <span
                            key={field}
                            className="inline-flex items-center rounded-full bg-background-200 px-2 py-0.5 text-[11px] text-foreground-700"
                          >
                            {fieldLabel(field)}
                          </span>
                        ))
                      ) : (
                        <span className="text-[11px] text-foreground-400">{t("helpFeedback.diffNoChange")}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : version.id)}
                    className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-700 transition-colors hover:border-primary-300"
                  >
                    <span className="flex h-3 w-3 items-center justify-center">
                      <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                    </span>
                    {t("helpFeedback.diffToggle")}
                  </button>
                  <button
                    type="button"
                    onClick={() => onRollback(version)}
                    disabled={saving}
                    className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-2.5 py-1 text-[11px] font-medium text-accent-700 transition-colors hover:bg-accent-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="flex h-3 w-3 items-center justify-center">
                      <i className="ri-history-line" />
                    </span>
                    {t("helpFeedback.rollback")}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="space-y-2 border-t border-background-200 px-4 py-3">
                  {changed.length === 0 ? (
                    <p className="text-xs text-foreground-500">{t("helpFeedback.diffNoChange")}</p>
                  ) : (
                    changed.map((field) => (
                      <div key={field} className="rounded-md border border-background-200 bg-background-100 px-3 py-2">
                        <p className="text-[11px] font-medium text-foreground-700">{fieldLabel(field)}</p>
                        <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2">
                          <div className="rounded-md bg-accent-50 px-2.5 py-1.5">
                            <span className="text-[10px] uppercase tracking-wide text-accent-700">
                              {t("helpFeedback.diffBefore")}
                            </span>
                            <p className="mt-0.5 break-words text-xs leading-relaxed text-foreground-700 line-through decoration-foreground-400/60">
                              {contentValue(field, older)}
                            </p>
                          </div>
                          <div className="rounded-md bg-primary-50 px-2.5 py-1.5">
                            <span className="text-[10px] uppercase tracking-wide text-primary-700">
                              {t("helpFeedback.diffAfter")}
                            </span>
                            <p className="mt-0.5 break-words text-xs leading-relaxed text-foreground-900">
                              {contentValue(field, version)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}